import * as THREE from 'three';

/**
 * Manages the Three.js camera
 */
export class CameraManager {
  private camera: THREE.PerspectiveCamera;
  private targetPosition: THREE.Vector3 | null = null;
  private targetLookAt: THREE.Vector3 | null = null;
  private transitionSpeed: number = 0.05;
  private isTransitioning: boolean = false;

  /**
   * Creates a new CameraManager instance
   * @param camera The Three.js camera to manage
   */
  constructor(camera: THREE.PerspectiveCamera) {
    this.camera = camera;
  }

  /**
   * Sets the camera position immediately
   * @param position The new position
   */
  public setPosition(position: THREE.Vector3): void {
    this.camera.position.copy(position);
    this.targetPosition = null;
    this.isTransitioning = false;
  }

  /**
   * Sets the camera position with a smooth transition
   * @param position The target position
   * @param speed The transition speed (0-1)
   */
  public transitionToPosition(position: THREE.Vector3, speed: number = 0.05): void {
    this.targetPosition = position.clone();
    this.transitionSpeed = Math.max(0.001, Math.min(1, speed));
    this.isTransitioning = true;
  }

  /**
   * Makes the camera look at a specific point immediately
   * @param target The point to look at
   */
  public lookAt(target: THREE.Vector3): void {
    this.camera.lookAt(target);
    this.targetLookAt = null;
  }

  /**
   * Makes the camera look at a specific point with a smooth transition
   * @param target The point to look at
   * @param speed The transition speed (0-1)
   */
  public transitionLookAt(target: THREE.Vector3, speed: number = 0.05): void {
    this.targetLookAt = target.clone();
    this.transitionSpeed = Math.max(0.001, Math.min(1, speed));
    this.isTransitioning = true;
  }

  /**
   * Sets the camera field of view
   * @param fov The new field of view in degrees
   */
  public setFOV(fov: number): void {
    this.camera.fov = Math.max(10, Math.min(120, fov));
    this.camera.updateProjectionMatrix();
  }

  /**
   * Sets the camera aspect ratio
   * @param aspect The new aspect ratio
   */
  public setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Updates the camera position and rotation
   * @param deltaTime The time elapsed since the last update
   */
  public update(deltaTime: number): void {
    if (!this.isTransitioning) return;

    let stillTransitioning = false;

    // Handle position transition
    if (this.targetPosition) {
      // Calculate the interpolation factor
      const lerpFactor = 1 - Math.pow(1 - this.transitionSpeed, deltaTime * 60);
      
      // Interpolate the position
      this.camera.position.lerp(this.targetPosition, lerpFactor);
      
      // Check if we're close enough to the target
      if (this.camera.position.distanceTo(this.targetPosition) < 0.01) {
        this.camera.position.copy(this.targetPosition);
        this.targetPosition = null;
      } else {
        stillTransitioning = true;
      }
    }

    // Handle look-at transition
    if (this.targetLookAt) {
      // Get the current look-at direction
      const currentDirection = new THREE.Vector3(0, 0, -1);
      currentDirection.applyQuaternion(this.camera.quaternion);
      
      // Calculate the target direction
      const targetDirection = new THREE.Vector3().subVectors(this.targetLookAt, this.camera.position).normalize();
      
      // Create a quaternion for the target rotation
      const targetQuaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, -1), targetDirection);
      
      // Interpolate the quaternion
      const lerpFactor = 1 - Math.pow(1 - this.transitionSpeed, deltaTime * 60);
      this.camera.quaternion.slerp(targetQuaternion, lerpFactor);
      
      // Check if we're close enough to the target rotation
      const dot = currentDirection.dot(targetDirection);
      if (dot > 0.9999) {
        this.camera.lookAt(this.targetLookAt);
        this.targetLookAt = null;
      } else {
        stillTransitioning = true;
      }
    }

    // Update transition state
    this.isTransitioning = stillTransitioning;
  }

  /**
   * Gets the camera
   * @returns The Three.js camera
   */
  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  /**
   * Checks if the camera is currently transitioning
   * @returns True if the camera is transitioning
   */
  public isInTransition(): boolean {
    return this.isTransitioning;
  }
} 